import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  clearAllNotificationAlerts,
  dismissNotification,
  dismissNotificationAlert,
  dismissNotificationAlertsByKeys,
  performBulkAction
} from '../../store/notificationSlice';
import { setActiveConversation, fetchMessages, addVirtualConversation } from '../../store/messagesSlice';
import { XMarkIcon, UserGroupIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Conversation } from '../../types/message';
import { useSoundPreferences } from '../../hooks/useSoundPreferences';

interface NotificationAlert {
  id: string;
  type: 'message' | 'group_message';
  title: string;
  message: string;
  senderName: string;
  conversationId?: string;
  groupId?: number;
  messageId?: string;
  timestamp: number;
  urgencyLevel?: 'routine' | 'urgent' | 'critical';
  urgencyReason?: string;
  forceSound?: boolean;
}

interface NotificationCardProps {
  alert: NotificationAlert;
  index: number;
  onDismiss: (alert: NotificationAlert) => void;
  onView: (alert: NotificationAlert) => void;
}

// Get urgency border color for minimal left accent
const getUrgencyBorderColor = (urgencyLevel: string): string => {
  switch (urgencyLevel) {
    case 'critical':
      return 'border-l-red-500';
    case 'urgent':
      return 'border-l-orange-500';
    case 'routine':
    default:
      return 'border-l-blue-500';
  }
};

// Get avatar background class based on urgency level
const getAvatarBgClass = (urgencyLevel: string, isGroupMessage: boolean): string => {
  if (isGroupMessage) return 'bg-purple-500';
  
  switch (urgencyLevel) {
    case 'critical':
      return 'bg-red-500';
    case 'urgent':
      return 'bg-orange-500';
    case 'routine':
    default:
      return 'bg-blue-500';
  }
};

const NotificationCard: React.FC<NotificationCardProps> = ({ alert, index, onDismiss, onView }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Animate in after a short delay based on index
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, index * 50);
    return () => clearTimeout(timer);
  }, [index]);

  const isGroupMessage = alert.type === 'group_message';
  const urgencyLevel = alert.urgencyLevel || 'routine';
  const isCritical = urgencyLevel === 'critical';
  const urgencyBorderColor = getUrgencyBorderColor(urgencyLevel);
  const avatarBgClass = getAvatarBgClass(urgencyLevel, isGroupMessage);
  
  // Format time ago
  const getTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div 
      className={`
        backdrop-blur-md bg-white/80 dark:bg-gray-900/80 
        border border-gray-200/50 dark:border-gray-700/50 
        border-l-2 ${urgencyBorderColor}
        shadow-xl rounded-lg
        transition-all duration-300 ease-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        cursor-pointer hover:shadow-2xl hover:bg-white/90 dark:hover:bg-gray-900/90
      `}
      onClick={() => onView(alert)}
      role={isCritical ? 'alert' : 'status'}
      aria-live={isCritical ? 'assertive' : 'polite'}
      aria-label={`${urgencyLevel === 'routine' ? '' : urgencyLevel.toUpperCase() + ' '} message from ${alert.senderName}`}
    >
      {/* Compact single-line layout - responsive padding */}
      <div className="flex items-center gap-2.5 p-2 sm:p-2.5">
        {/* Avatar */}
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold ${avatarBgClass}`}>
          {isGroupMessage ? (
            <UserGroupIcon className="h-3.5 w-3.5" />
          ) : (
            alert.senderName.charAt(0).toUpperCase()
          )}
        </div>
        
        {/* Message content - compact */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {alert.senderName}
            </p>
            <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
              {getTimeAgo(alert.timestamp)}
            </span>
          </div>
          
          {/* Group indicator (if group message) */}
          {isGroupMessage && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 truncate">
              {alert.title.replace('New message in ', '')}
            </p>
          )}
          
          {/* Message preview - single line */}
          <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-1 truncate">
            {alert.message}
          </p>
        </div>
        
        {/* Action buttons - minimal icon buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* View button - small icon */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView(alert);
            }}
            className="w-8 h-8 rounded-full bg-blue-500/10 dark:bg-blue-500/20 hover:bg-blue-500/20 dark:hover:bg-blue-500/30 flex items-center justify-center transition-colors"
            aria-label="View message"
          >
            <ArrowRightIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </button>
          
          {/* Dismiss button - small X icon */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsVisible(false);
              setTimeout(() => onDismiss(alert), 300);
            }}
            className="w-8 h-8 rounded-full hover:bg-gray-200/50 dark:hover:bg-gray-700/50 flex items-center justify-center transition-colors"
            aria-label="Dismiss notification"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const GlobalNotificationAlert: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const activeAlerts = useAppSelector(state => state.notifications.activeAlerts);
  const notifications = useAppSelector(state => state.notifications.notifications);
  const conversations = useAppSelector(state => state.messages.conversations);
  const { playNotificationSound } = useSoundPreferences();
  
  // Track previously seen alerts to only play sounds for new ones
  const previousAlertIdsRef = useRef<Set<string>>(new Set());

  // Play sounds for new alerts
  useEffect(() => {
    const currentAlertIds = new Set(activeAlerts.map(alert => alert.id));
    
    // Find new alerts that weren't in the previous set
    activeAlerts.forEach(alert => {
      if (!previousAlertIdsRef.current.has(alert.id)) {
        // This is a new alert - play sound based on urgency
        const urgencyLevel = alert.urgencyLevel || 'routine';
        
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log(`🔊 Playing notification sound for new alert: ${alert.id}, urgency: ${urgencyLevel}`);
        }
        
        // Play the appropriate sound
        playNotificationSound(
          urgencyLevel as 'routine' | 'urgent' | 'critical',
          { force: alert.forceSound === true }
        );
      }
    });
    
    // Update the set of seen alert IDs
    previousAlertIdsRef.current = currentAlertIds;
  }, [activeAlerts, playNotificationSound]);

  const resolvePersistedNotificationId = (alert: NotificationAlert) => {
    const notificationIdFromAlert = alert.id.startsWith('notification-')
      ? alert.id.replace('notification-', '')
      : alert.id.startsWith('msg_')
        ? null
        : alert.id;

    const matchingNotification = notifications.find(notification =>
      (!!alert.messageId && notification.metadata?.messageId === alert.messageId) ||
      (!!notificationIdFromAlert && notification.id === notificationIdFromAlert)
    );

    return matchingNotification?.id || null;
  };

  const handleDismiss = async (alert: NotificationAlert) => {
    const persistedNotificationId = resolvePersistedNotificationId(alert);

    if (persistedNotificationId) {
      try {
        await dispatch(dismissNotification(persistedNotificationId)).unwrap();
        return;
      } catch (error) {
        console.error('Failed to dismiss persisted notification from popup:', error);
      }
    }

    dispatch(dismissNotificationAlert(alert.id));
  };

  const handleDismissAll = async () => {
    const matchedNotificationIds = Array.from(new Set(
      activeAlerts
        .map(alert => resolvePersistedNotificationId(alert))
        .filter((notificationId): notificationId is string => Boolean(notificationId))
    ));

    const unmatchedAlertIds = activeAlerts
      .filter(alert => !resolvePersistedNotificationId(alert))
      .map(alert => alert.id);

    if (matchedNotificationIds.length > 0) {
      try {
        await dispatch(performBulkAction({
          notificationIds: matchedNotificationIds,
          action: 'delete'
        })).unwrap();
      } catch (error) {
        console.error('Failed to dismiss all persisted popup notifications:', error);
      }
    }

    if (unmatchedAlertIds.length > 0) {
      dispatch(dismissNotificationAlertsByKeys({ alertIds: unmatchedAlertIds }));
    }

    if (matchedNotificationIds.length === 0 && unmatchedAlertIds.length === activeAlerts.length) {
      dispatch(clearAllNotificationAlerts());
    }
  };

  const handleView = (alert: NotificationAlert) => {
    if (alert.conversationId) {
      const isGroupMessage = alert.type === 'group_message';
      
      // For group messages, ensure the conversation exists
      if (isGroupMessage && alert.groupId) {
        const conversationId = alert.conversationId;
        const existingConversation = conversations.find(conv => conv.id === conversationId);
        
        if (!existingConversation) {
          // Create a virtual conversation for the group
          const virtualConversation: Conversation = {
            id: conversationId,
            type: 'group',
            name: alert.title.replace('New message in ', '') || 'Group Chat',
            participants: [],
            unread_count: 0,
            last_message: undefined
          };
          dispatch(addVirtualConversation(virtualConversation));
        }
        
        // Fetch messages for the group conversation
        dispatch(fetchMessages({ conversationId }));
      }
      
      dispatch(setActiveConversation(alert.conversationId));
      navigate('/onecare/messages');
      dispatch(dismissNotificationAlert(alert.id));
    }
  };

  if (activeAlerts.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-xs w-full sm:max-w-xs">
      {/* Container for stacked notifications - minimal spacing */}
      <div className="space-y-1.5">
        {activeAlerts.map((alert, index) => (
          <NotificationCard
            key={alert.id}
            alert={alert}
            index={index}
            onDismiss={handleDismiss}
            onView={handleView}
          />
        ))}
      </div>
      
      {/* Show count if more than 3 notifications - minimal style */}
      {activeAlerts.length > 3 && (
        <div className="text-center mt-2">
          <span className="text-xs text-gray-600 dark:text-gray-400 backdrop-blur-md bg-white/60 dark:bg-gray-900/60 px-2.5 py-1 rounded-full border border-gray-200/50 dark:border-gray-700/50">
            {activeAlerts.length} more
          </span>
        </div>
      )}
      
      {/* Clear all button if multiple notifications - minimal style */}
      {activeAlerts.length > 1 && (
        <div className="text-center mt-2">
          <button
            onClick={() => {
              void handleDismissAll();
            }}
            className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 backdrop-blur-md bg-white/60 dark:bg-gray-900/60 px-2.5 py-1 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-white/80 dark:hover:bg-gray-900/80 transition-all"
          >
            Dismiss All
          </button>
        </div>
      )}
    </div>
  );
};
